import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import { queryKeys } from "@/lib/data/static";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createMatterLien,
  deleteMatterLien,
  updateMatterLien,
} from "@/lib/data/requests";
import {
  type APIError,
  type APIResponse,
  type Matter,
  type MatterLien,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { type AxiosError } from "axios";
import dayjs from "dayjs";
import toast from "react-hot-toast";

const updateMatterLiens = (
  oldData: APIResponse<Matter> | undefined,
  updateLiens: (liens: Matter["liens"]) => Matter["liens"],
) => {
  if (!oldData) return;

  const liens = updateLiens(oldData.data.liens);

  return {
    ...oldData,
    data: {
      ...oldData.data,
      liens,
    },
  };
};

export const useCreateMatterLien = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterLien>[0]) =>
      createMatterLien(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) =>
          updateMatterLiens(oldData, (liens) => [
            ...liens,
            {
              ...data,
              dateCreated: dayjs().utc().format("YYYY-MM-DDTHH:mm:ss"),
              dateModified: dayjs().utc().format("YYYY-MM-DDTHH:mm:ss"),
              id: "optimistic",
            } as MatterLien,
          ]),
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error creating lien");
    },

    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Lien created successfully");
      }
    },
  });
};

export const useUpdateMatterLien = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterLien>[0]) =>
      updateMatterLien(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) =>
          updateMatterLiens(oldData, (liens) =>
            liens.map((lien) =>
              lien.id === data.id
                ? {
                    ...lien,
                    ...data,
                    dateModified: dayjs().utc().format("YYYY-MM-DDTHH:mm:ss"),
                  }
                : lien,
            ),
          ),
      );

      return {
        previousData,
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating lien");
    },

    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Lien updated successfully");
      }
    },
  });
};

export const useDeleteMatterLien = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterLien>[0]) =>
      deleteMatterLien(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) =>
          updateMatterLiens(oldData, (liens) =>
            liens.filter((lien) => lien.id !== data.id),
          ),
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: async (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
      toastAPIErrorMessage(error, "Error deleting lien");
    },

    onSettled: async (_data, _error, variables) => {
      if (_error) return;
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      toast.success("Lien deleted successfully");
    },
  });
};
